<template>
  <div>
    <div v-if="assignDialog"
         :class="$forms.overlay"
         @click="closeDialog($event)">
      <v-dialog :value="assignDialog"
                width="500"
                :hide-overlay="true">
        <div :class="$style.assignPopupHeader">
          <button :class="$style.closeBtn"
                  @click.prevent="closeDialog($event)">
            <app-svg-icon :class="$style.closeIcon"
                          name="ic_close_black" />
          </button>
          <h2>Assign {{ role }}</h2>
          <h5>Client:</h5>
          <h4>{{ clientFullName }}</h4>
        </div>

        <form :class="$style.assignForm"
              @submit.prevent="submitAssign">
          <v-select :items="getAgents"
                    label="Please select"
                    :value="getSelectedAgent"
                    @change="chooseAgent">
            <template v-slot:item="data" >
              <div :class="$style.agentItem">
                <div :class="$style.agentName">
                  {{ data.item.fullName }}
                </div>
                <div :class="$style.agentClients">
                  {{ data.item.numberOfClients }} clients
                </div>
              </div>
            </template>

            <template v-slot:selection="data" >
              {{ data.item.fullName}}
            </template>
          </v-select>
          <app-button :class="$style.assignBtn"
                      button-style="accent"
                      text="Assign"
                      type="submit"
                      :disabled="getDisableButton" />
          <p :class="getStatusMessageStyle">{{ statusText }}</p>
        </form>
        <app-loader :class="$style.assignLoader"
                    :show-loader="loading"></app-loader>
      </v-dialog>
    </div>
  </div>

</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { LOADING_STATUSES } from '@/constants';

const ADMIN_ROLE = 'Admin';
const COACH_ROLE = 'Coach';
const NEGOTIATOR_ROLE = 'Negotiator';
const delayForClosingPopup = 2000;

export default {
  props: {
    role: {
      type: String,
      required: true,
    },
    clientId: {
      type: Number,
      required: true,
    },
    clientFullName: {
      type: String,
      required: true,
    },
    assignDialog: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      agents: [],
      selectedAgent: null,
      loading: false,
      status: '',
      statusText: '',
      disableButton: false,
    };
  },
  methods: {
    ...mapActions([
      'fetchAllAgentsActive',
      'assignCoach',
      'assignNegotiator',
    ]),
    closeDialog(e) {
      this.$emit('close', e);
    },
    chooseAgent(agent) {
      this.selectedAgent = agent;
    },
    fetchAgentsOfTheAppropriateRole() {
      this.loading = true;
      this.fetchAllAgentsActive()
        .then(() => {
          if (this.role === COACH_ROLE) {
            this.agents = this.getAllAgentsActive.items;
          } else {
            this.agents = this.getAllAgentsActive.items.filter(
              agent => agent.roleName === ADMIN_ROLE || agent.roleName === NEGOTIATOR_ROLE,
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitAssign() {
      this.loading = true;
      this.disableButton = true;
      this.status = '';
      const agentId = this.selectedAgent.id;
      const data = {
        agentId,
        agentName: this.selectedAgent.fullName,
        clientAppId: this.clientId,
      };
      switch (this.role) {
        case COACH_ROLE:
          this.assignCoachAgent(data);
          break;
        case NEGOTIATOR_ROLE:
          this.assignNegotiatorAgent(data);
          break;
        default:
          break;
      }
    },
    assignCoachAgent(data) {
      this.assignCoach(data)
        .then(() => {
          console.log('success');
          this.status = LOADING_STATUSES.SUCCESS;
          this.statusText = 'The agent was successfully assigned.';
          setTimeout(() => {
            this.status = '';
            this.closeDialog();
            this.$emit('change');
          }, delayForClosingPopup);
        })
        .catch((err) => {
          console.log(err);
          this.disableButton = false;
          this.statusText = 'Error';
          this.status = LOADING_STATUSES.ERROR;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    assignNegotiatorAgent(data) {
      this.assignNegotiator(data)
        .then(() => {
          console.log('success');
          this.status = LOADING_STATUSES.SUCCESS;
          this.statusText = 'The agent was successfully assigned.';
          setTimeout(() => {
            this.status = '';
            this.closeDialog();
            this.$emit('change');
          }, delayForClosingPopup);
        })
        .catch((err) => {
          console.log(err);
          this.disableButton = false;
          this.statusText = 'Error';
          this.status = LOADING_STATUSES.ERROR;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    ...mapGetters([
      'getAllAgentsActive',
    ]),
    getAgents() {
      return this.agents;
    },
    getSelectedAgent() {
      return this.selectedAgent;
    },
    getStatusMessageStyle() {
      if (this.status === LOADING_STATUSES.SUCCESS) {
        return [this.$style.statusText, this.$style.success];
      }
      if (this.status === LOADING_STATUSES.ERROR) {
        return [this.$style.statusText, this.$style.error];
      }

      return this.$style.statusText;
    },
    getDisableButton() {
      return this.disableButton || !this.selectedAgent;
    },
  },
  watch: {
    assignDialog() {
      if (this.assignDialog) {
        this.fetchAgentsOfTheAppropriateRole();
      } else {
        this.selectedAgent = '';
        this.agents = [];
        this.status = '';
      }
    },
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
<style lang="scss" module>
@import "@/styles/variables.scss";

.assignPopupHeader {
  padding: 70px 40px 20px;
  text-align: left;
  position: relative;

  .closeBtn {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 16px;
    height: 16px;
    border: none;
    outline: none;
  }

  .closeIcon {
    width: 16px;
    height: 16px;
  }

  h2 {
    font-weight: 400;
    font-style: normal;
    color: $adminPortalTextColor;
    font-size: 1.8rem;
    text-align: center;
    margin-bottom: 30px;
  }

  h4,
  h5 {
    font-size: 1.4rem;
    color: $main-text-color;
  }

  h4 {
    font-weight: 700;
  }
}

.assignForm {
  padding: 0 40px 40px;
}

.assignBtn {
  padding: 8px 60px;
  margin-top: 20px;
}

.assignLoader {
  z-index: 500;
  margin-left: 0;
}

.statusText {
  display: none;
  margin-top: 20px;
  padding: 6px 40px;
  color: $white-color;
  text-align: center;

  &.success {
    display: block;
    color: $successColor;
  }

  &.error {
    display: block;
    color: $errorColor;
  }
}

.agentItem {
  width: 100%;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid $adminTablesColor;

  .agentName {
    color: $main-text-color;
    font-size: 1.4rem;
  }

  .agentClients {
    color: $adminPortalTextColor;
    font-size: 1rem;
  }
}
</style>
